import { create } from 'zustand';
import { FamilyMember, SharedLink, ActionHistory, PersonalInfo, AddRequest, PersonalInfoRequest } from '@/types';
import { getAppData, setData } from '@/services/local/storage';
import { addChildToTree } from '@/utils/family/tree';

interface FamilyState {
  members: FamilyMember[];
  sharedLinks: SharedLink[];
  actionHistory: ActionHistory[];
  personalInfos: PersonalInfo[];
  requests: AddRequest[];
  personalInfoRequests: PersonalInfoRequest[];
  
  // Member actions
  addMember: (member: FamilyMember) => void;
  updateMember: (id: string, updates: Partial<FamilyMember>) => void;
  deleteMember: (id: string) => void;
  addChild: (parentId: string, childName: string) => void;
  
  // Request actions
  addRequest: (request: AddRequest) => void;
  addPersonalInfoRequest: (request: PersonalInfoRequest) => void;
  updateRequestStatus: (id: string, status: AddRequest['status']) => void;
  updatePersonalInfoRequestStatus: (id: string, status: PersonalInfoRequest['status']) => void;
  
  // Personal info actions
  addPersonalInfo: (info: PersonalInfo) => void;
  updatePersonalInfo: (id: string, updates: Partial<PersonalInfo>) => void;
  deletePersonalInfo: (id: string) => void;
  
  // Shared link actions
  getSharedLink: (token: string) => SharedLink | null;
}

const initialData = getAppData();

export const useFamilyStore = create<FamilyState>((set, get) => ({
  // Initialize state
  members: initialData.members || [],
  sharedLinks: initialData.sharedLinks || [],
  actionHistory: initialData.actionHistory || [],
  personalInfos: initialData.personalInfos || [],
  requests: initialData.requests || [],
  personalInfoRequests: initialData.personalInfoRequests || [],

  // Member actions
  addMember: (member) => {
    set((state) => {
      const newMembers = [...state.members, member];
      setData('app_data', { ...get(), members: newMembers });
      return { members: newMembers };
    });
  },

  updateMember: (id, updates) => {
    set((state) => {
      const newMembers = state.members.map(member =>
        member.id === id ? { ...member, ...updates } : member
      );
      setData('app_data', { ...get(), members: newMembers });
      return { members: newMembers };
    });
  },

  deleteMember: (id) => {
    set((state) => {
      // Get all descendants
      const getDescendants = (nodeId: string): string[] => {
        const node = state.members.find(m => m.id === nodeId);
        if (!node || !node.children.length) return [nodeId];
        return [nodeId, ...node.children.flatMap(childId => getDescendants(childId))];
      };

      const idsToDelete = getDescendants(id);
      const newMembers = state.members.filter(m => !idsToDelete.includes(m.id));
      setData('app_data', { ...get(), members: newMembers });
      return { members: newMembers };
    });
  },

  addChild: (parentId, childName) => {
    set((state) => {
      const { newChild, updatedParent } = addChildToTree(parentId, childName, state.members);
      
      let newMembers = [...state.members, newChild];
      if (updatedParent) {
        newMembers = newMembers.map(m => 
          m.id === updatedParent.id ? updatedParent : m
        );
      }

      setData('app_data', { ...get(), members: newMembers });
      return { members: newMembers };
    });
  },

  // Request actions
  addRequest: (request) => {
    set((state) => {
      const newRequests = [...state.requests, request];
      setData('app_data', { ...get(), requests: newRequests });
      return { requests: newRequests };
    });
  },

  addPersonalInfoRequest: (request) => {
    set((state) => {
      const newRequests = [...state.personalInfoRequests, request];
      setData('app_data', { ...get(), personalInfoRequests: newRequests });
      return { personalInfoRequests: newRequests };
    });
  },

  updateRequestStatus: (id, status) => {
    set((state) => {
      const newRequests = state.requests.map(request =>
        request.id === id ? { ...request, status } : request
      );
      setData('app_data', { ...get(), requests: newRequests });
      return { requests: newRequests };
    });
  },

  updatePersonalInfoRequestStatus: (id, status) => {
    set((state) => {
      const newRequests = state.personalInfoRequests.map(request =>
        request.id === id ? { ...request, status } : request
      );
      setData('app_data', { ...get(), personalInfoRequests: newRequests });
      return { personalInfoRequests: newRequests };
    });
  },

  // Personal info actions
  addPersonalInfo: (info) => {
    set((state) => {
      const newInfos = [...state.personalInfos, info];
      setData('app_data', { ...get(), personalInfos: newInfos });
      return { personalInfos: newInfos };
    });
  },

  updatePersonalInfo: (id, updates) => {
    set((state) => {
      const newInfos = state.personalInfos.map(info =>
        info.id === id ? { ...info, ...updates } : info
      );
      setData('app_data', { ...get(), personalInfos: newInfos });
      return { personalInfos: newInfos };
    });
  },

  deletePersonalInfo: (id) => {
    set((state) => {
      const newInfos = state.personalInfos.filter(info => info.id !== id);
      setData('app_data', { ...get(), personalInfos: newInfos });
      return { personalInfos: newInfos };
    });
  },

  // Shared link actions
  getSharedLink: (token) => {
    const link = get().sharedLinks.find(l => l.token === token);
    if (!link || !link.isActive) return null;
    
    if (link.maxUses && link.useCount >= link.maxUses) {
      return null;
    }

    return link;
  },
}));